import React, { useCallback, useEffect, useState } from "react";
import {
    Box,
    Button,
    Card,
    Dialog,
    DialogActions,
    DialogContent,
    Divider,
    IconButton,
    List,
    ListItem,
    Tooltip,
    Typography
} from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import type { Consultation, Patient, PatientFilter } from "../../../../../services/patient.service";
import { getPatientsPaginated2 } from "../../../../../services/patient.service";
import { PatientTable } from "./patient-table";
import { handlePromise } from "../../../../../utils/handlePromisse";
import { initialPagination, PaginatedResponse } from "../../../../../domain/models/paginated-response.model";
import { SimpleSnackbar } from "../../../../../components/snackbar";
import { Loading } from "../../../../../components/loading";
import Space from "../../../../../components/Space";
import { Title } from "../../../../../theme/globalStyles";

export type DuplicatePatientState = {
    id: number;
    name: string;
}

interface DuplicateConsultDialogProps {
    open: boolean;
    onClose: () => void;
    consult: Consultation;
    onDuplicate: (consultationId: number, patients: DuplicatePatientState[], type: string) => void;
}

export const initialState: {open: boolean; consult: Consultation} = {
    open: false,
    consult: { id: 0, date: '', name: '', type: '' },
}

enum EnumDuplicateStep {
    SELECTION = 'SELECTION',
    CONFIRMATION = 'CONFIRMATION',
}

const DuplicateConsultDialog: React.FC<DuplicateConsultDialogProps> = (props) => {
    const [selectedPatients, setSelectedPatients] = useState<DuplicatePatientState[]>([]);
    const [filter, setFilter] = useState<PatientFilter>({ page: 1, size: 5 })
    const [step, setStep] = useState<EnumDuplicateStep>(EnumDuplicateStep.SELECTION);
    const [patients, setPatients] = useState<PaginatedResponse<Patient>>(initialPagination)

    const [loading, setLoading] = useState(false)
    const [showSnackbar, setShowSnackbar] = useState(false)
    const [snackbarMessage, setSnackbarMessage] = useState('')

    const handleTogglePatient = (patientId: number, name: string) => {
        const alreadySelected = selectedPatients.findIndex(sp => sp.id === patientId);
        if (alreadySelected >= 0) {
            setSelectedPatients(selectedPatients.filter(sp => sp.id !== patientId));
        } else {
            setSelectedPatients([...selectedPatients, { id: patientId, name }]);
        }
    }

    const handleConfirm = () => {
        props.onDuplicate(props.consult.id, selectedPatients, props.consult.type);
        setSelectedPatients([]);
        setStep(EnumDuplicateStep.SELECTION);
        props.onClose();
    };

    const handleCancel = () => {
        setSelectedPatients([]);
        setStep(EnumDuplicateStep.SELECTION);
        props.onClose();
    };

    const fetchPatients = useCallback(async () => {
        setLoading(true)
        const [result, error] = await handlePromise(getPatientsPaginated2(filter))
        setLoading(false)

        if (!!error) {
            setShowSnackbar(true)
            setSnackbarMessage(error.response.data.message)
            return
        }

        setPatients(result.data as PaginatedResponse<Patient>)
    }, [filter])

    useEffect(() => {
        if (!props.open) return
        fetchPatients()
    }, [fetchPatients, props.open])

    const handlePageChange = useCallback((newPage: number, size: number) => {
        setFilter({ ...filter, page: newPage, size })
    }, [filter])

    const handleSizeChange = useCallback((newSize: number, page: number) => {
        setFilter({ ...filter, page, size: newSize })
    }, [filter])

    const handleFilter = useCallback((query?: string) => {
        setFilter({ ...filter, search: query })
    }, [filter])

    const handleRemoveSelected = (id: number) => {
        setSelectedPatients(prev => prev.filter(p => p.id !== id))
    }

    return (
        <Dialog open={props.open} onClose={handleCancel} fullWidth={true}>
            <Loading open={loading}/>
            <SimpleSnackbar
                show={showSnackbar}
                message={snackbarMessage}
                onDismiss={() => setShowSnackbar(false)}
            />

            <DialogContent>
                {step === EnumDuplicateStep.SELECTION ? (
                    <>
                        <Title style={{fontSize: '0.8rem'}}>Selecione pacientes para duplicar a consulta</Title>
                        <PatientTable
                            patients={patients.data}
                            query={filter.search || ''}
                            onQueryChange={handleFilter}
                            selectablesPatients={selectedPatients}
                            page={patients.pagination.currentPage}
                            size={patients.pagination.perPage}
                            total={patients.pagination.total}
                            onPageChange={handlePageChange}
                            onSizeChange={handleSizeChange}
                            onSelectedPatient={handleTogglePatient}
                        />
                    </>
                ) : (
                    <>
                        <div style={{ marginBottom: 12 }}>
                            {"Você está duplicando a consulta "}<strong>{props.consult.name}</strong>{` para ${selectedPatients.length} paciente(s).`}
                        </div>

                        <Space pixels={16} direction={'v'}/>

                        <Card variant="outlined" sx={{ width: '100%', maxWidth: 480 }}>
                            <List sx={{ width: '100%', padding: 0 }}>
                                {selectedPatients.map((sp, idx) => (
                                    <React.Fragment key={sp.id}>
                                        <ListItem disableGutters sx={{ px: 2, py: 1 }}>
                                            <Box
                                                sx={{
                                                    display: 'grid',
                                                    gridTemplateColumns: '1fr auto',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    width: '100%',
                                                }}
                                            >
                                                <Typography
                                                    variant="body1"
                                                    sx={{
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        pr: 1
                                                    }}
                                                >
                                                    {sp.name || '(sem nome)'}
                                                </Typography>

                                                <Tooltip title="Remover" placement="top">
                                                    <IconButton
                                                        size="small"
                                                        edge="end"
                                                        aria-label="Remover paciente"
                                                        onClick={() => handleRemoveSelected(sp.id)}
                                                    >
                                                        <DeleteIcon fontSize="small"/>
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </ListItem>

                                        {idx < selectedPatients.length - 1 && <Divider component="li"/>}
                                    </React.Fragment>
                                ))}
                            </List>
                        </Card>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCancel}>Cancelar</Button>
                {step === EnumDuplicateStep.SELECTION ? (
                    <Button onClick={() => setStep(EnumDuplicateStep.CONFIRMATION)}
                            disabled={selectedPatients.length === 0} color="primary"
                            variant="contained">Próximo</Button>
                ) : (
                    <>
                        <Button onClick={() => setStep(EnumDuplicateStep.SELECTION)}>Voltar</Button>
                        <Button onClick={handleConfirm} disabled={selectedPatients.length === 0} color="primary"
                                variant="contained">Duplicar</Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default DuplicateConsultDialog;
